import { useParams } from "react-router-dom";
import { useUserContext } from "../providers/UserContext";
import { Button, getButtonClassName } from "../components/common/Button";
import { useEffect, useState } from "react";
import { viewGroup } from "../lib/database";
import {Activity, Chat, UnreadChat} from "../lib/types";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { Icon } from "../components/common/Icon";
import { Empty } from "../components/illustrations/empty";
import { List } from "../components/chat/List";
import { useChatListContext } from "../providers/ChatListProvider";
import { MembersModal } from "../components/groups/MembersModal";
import { useModalContext } from "../providers/ModalProvider";
import { getItem, setItem, mergeChats } from "../lib/localStorage";
import {GroupName} from "../components/common/GroupName";

export const Group = ({}: {}) => {
  const { id } = useParams();
  const { user, groups, activeWorkspace, setActiveChatId, unreadChats, activity } = useUserContext();
  const { setChatListSettings, setChatListVisible, setLoadChats, setCurrentFilter } = useChatListContext();
  const group = groups.find((group) => group.id === id);
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastLoadedChatTimestamp, setLastLoadedChatTimestamp] = useState<number>(
    getItem(`lastLoadedChatTimestamp_${id}`) || 0
  );

  useEffect(() => {
    setCurrentFilter("group_"+id || "");
    setChatListVisible(true);
    setActiveChatId("");
  }, [setChatListVisible, setActiveChatId, id]);

  const mergeGroupChats = async (newChats: Chat[]) => {
    const localChats = getItem(`groupChats_${id}`) || [];
    return mergeChats(localChats, newChats, (mergedChats) => {
      setItem(`groupChats_${id}`, mergedChats);
      setChats(mergedChats);
      if (newChats.length > 0) {
        setItem(`lastLoadedChatTimestamp_${id}`, Date.now());
        if (user && group) {
          viewGroup(user, group);
        }
      }
      setLastLoadedChatTimestamp(mergedChats[0]?.timestamp || 0);
      if (group) {
        setChatListSettings((<GroupName group={group} space={3} emojiClass={""}/>), []);
      }
      return mergedChats;
    });
  };

  const hasNewMessagesForGroup = (groupId: string, unreadChats: UnreadChat[], activity: Activity) => {
    const lastView = getItem(`lastLoadedChatTimestamp_${groupId}`) || 0;
    const unreadMessagesForGroup = unreadChats.filter(chat => chat?.groupPermissions && chat.groupPermissions[groupId]);
    const chats = getItem(`groupChats_${groupId}`) || [];
    if (chats && chats.length > 0 && chats.some((chat: Chat) => chat.title.toLowerCase().includes("generating title"))) {
      return true;
    }
    let myActivityForGroup = 0;
    if (activity.groups) {
      myActivityForGroup = activity.groups[groupId]?.lastMessage || 0;
    }
    return !lastView || unreadMessagesForGroup.length > 0 || myActivityForGroup > lastView;
  }

  const getChats = async (id:string, unreadChats: UnreadChat[], activity: Activity) => {
    const functions = getFunctions();

    const lastLoadedChatTimestamp = getItem(`lastLoadedChatTimestamp_${id}`) || 0
    const getUserChats = httpsCallable<
      { groupId?: string; timestamp?: number },
      { chats: { [key: string]: Chat } }
    >(functions, "getUserChats");
    try {
      if (!hasNewMessagesForGroup(id || "", unreadChats, activity)) {
        return mergeGroupChats([]);
      }
      const result = await getUserChats({
        groupId: id,
        timestamp: lastLoadedChatTimestamp + 1,
      });
      const fetchedChats = Object.values(result.data.chats);
      const resultChats = mergeGroupChats(fetchedChats);
      return resultChats;
    } catch (error) {
      console.error("Error fetching group chats:", error);
      return [];
    }
  };

  const loadChats = async (attempt = 0) => {
    if (id && user && group) {
      try {
        const chats = await getChats(id, unreadChats, activity);
        if (group) {
          setChatListSettings((<GroupName emojiClass="" group={group} space={3}/>), chats);
        }
        setLoading(false);
        return chats;
      } catch (error) {
        console.error("Error fetching group chats:", error);
      }
    }
  };

  const [loadGroups] = useState(Date.now());

  useEffect(() => {
    if (group) {
      loadChats();
      setLoadChats(() => (unreadChats: UnreadChat[], activity: Activity) => {
        return getChats(id || "", unreadChats, activity); // Ensure the function matches the expected signature
      });
    }
  }, [group, loadGroups]);

  useEffect(() => {
    setLoading(true);
  }, [group]);
  const { openModal } = useModalContext();
  const members = activeWorkspace?.members ? Object.values(activeWorkspace.members) : [];
  const groupMembers = members.filter(
    (member) =>
      group?.permissions[member.id] || group?.visibility === "public"
  );

  return (
    <div className="w-full h-full flex justify-center">
      {loading ? (
        <div className="flex flex-col gap-1 w-full h-full justify-center items-center">
          Loading...
        </div>
      ) : (
        <div className="w-full flex justify-center">
            <div className="flex flex-col gap-1 w-full h-full justify-center items-center">
              <div className="illustration">
                <Empty />
              </div>
              <div className="font-style-headline-1 flex items-center mt-[60px]">
                <span>
                  {group?.emoji} {group?.name}
                </span>
                <span className={"h-8 w-8 ml-2 mt-1 rounded-full bg-gray-8 block flex justify-center items-center"}>
                  <Icon
                    name={
                      group?.visibility === "public" ? "lock_open" : "lock"
                    }
                    size={"small"}
                  />
                </span>
              </div>
              {group?.visibility === "public" && (
                <div className={"font-style-body-md text-gray-3 italic"}>
                  This is a public group. Everyone will be able to see all new posts.
                </div>
              )}
              <div className="actions flex gap-2 mt-4">
                <Link to={`/group/${id}/compose`} className={getButtonClassName()}>
                  Create a new post
                </Link>
              </div>
            </div>
        </div>
      )}
    </div>
  );
};
